import { useMutation, useQueryClient } from 'react-query';

import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';
import { ChronologyFact } from '@/types';

interface CreateChronologyParameters {
  caseId: string | null;
  chronologyId: string | null;
  type: string | null;
  factIds?: string[];
}

interface HookProps {
  onSuccess: (data: CreateChronologyResponse) => void;
  onError: (data: ApiError) => void;
}

export interface CreateChronologyResponse {
  caseId: string;
  chronologyId: string;
  chronologyTitle: string;
  isNew: string;
  isProcessed: string;
  addedFacts?: ChronologyFact[];
}

export interface ApiError {
  statusCode: number;
  message: string;
}

export const useCreateChronology = ({ onSuccess, onError }: HookProps) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  const createChronology = useMutation(
    async ({ caseId, chronologyId, type, factIds }: CreateChronologyParameters) => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: {
          caseId,
          type,
          addToExisting: chronologyId !== null,
          existingChronologyId: chronologyId,
          factIds,
        },
      });
      const res = await fetch(`${APIBaseChronos}/api/case/${caseId}/chronology`, fetchConfig);
      if (!res.ok) {
        throw new Error('Network response was not ok');
      }
      return res.json(); // Return the response for use in onSuccess
    },
    {
      onSuccess: (data: CreateChronologyResponse) => {
        queryClient.invalidateQueries([QueryEntity.Chronologies, { caseId: data.caseId }]);
        queryClient.invalidateQueries([QueryEntity.Chronologies, { subsetId: data.caseId }]);
        queryClient.invalidateQueries([QueryEntity.MatterChronologies]);
        onSuccess(data);
      },
      onError: (error: ApiError) => {
        onError(error);
      },
    },
  );

  return {
    createChronology,
  };
};
